"use client"

import { usePathname, useSearchParams } from "next/navigation"
import { useEffect } from "react"

export function ScrollRestoration() {
  const pathname = usePathname()
  const searchParams = useSearchParams()

  // Reset to the top on a real page change only. A query-string change is NOT
  // one: pages that keep state in the URL (`/book`'s date + guest count,
  // filters) navigate with `{ scroll: false }` on purpose, and resetting here
  // would yank the diner back to the top on every tap.
  useEffect(() => {
    if (window.location.hash) return
    window.scrollTo(0, 0)
  }, [pathname])

  useEffect(() => {
    const hash = window.location.hash
    if (!hash) return

    const id = hash.slice(1)
    const scrollToEl = (el: Element) => {
      const header = document.querySelector("header")
      const offset = header ? header.getBoundingClientRect().height + 16 : 0
      const top = el.getBoundingClientRect().top + window.scrollY - offset
      window.scrollTo({ top, behavior: "smooth" })
    }

    const existing = document.getElementById(id)
    if (existing) {
      scrollToEl(existing)
      return
    }

    // On a soft navigation the target page can still be showing a Suspense
    // fallback (e.g. a streamed section) when this effect runs, so the section isn't in
    // the DOM yet and the deps won't change to re-run us. Watch for it to mount,
    // bounded so we don't observe forever if the hash never resolves.
    let cancelled = false
    const deadline = Date.now() + 3000
    const observer = new MutationObserver(() => {
      if (cancelled) return
      const el = document.getElementById(id)
      if (el) {
        observer.disconnect()
        scrollToEl(el)
      } else if (Date.now() > deadline) {
        observer.disconnect()
      }
    })
    observer.observe(document.body, { childList: true, subtree: true })
    return () => {
      cancelled = true
      observer.disconnect()
    }
  }, [pathname, searchParams])

  return null
}
